import React, { useEffect, useMemo, useRef, useState } from "react";
import usePulseLocale from "./i18n";
import { tokens, fmtCompact, fmtNum } from "./theme";
import { log2, chartDomain, chartScales, toPoints, placeLabels } from "./cityChartGeometry";

// Bucket palette — same semantics as the reference (nights per resident)
export const BUCKET_COLORS = {
  over_50: "#5B3A8A", "15_50": "#C8333F", "5_15": "#E8832A", "2_5": "#F3CF7A", under_2: "#CFC7B8",
};
export const BUCKET_KEYS = ["over_50", "15_50", "5_15", "2_5", "under_2"];
const MOVE = "transform .9s cubic-bezier(.4,0,.2,1), r .9s cubic-bezier(.4,0,.2,1), fill-opacity .3s, stroke .3s";

/** `domainRows` (optional) fixes axes + bubble scale across frames so the timeline animates on a stable grid. */
export const CityBubbleChart = ({ rows, domainRows, year, highlightIt, dm, onHover, onSelect, marked, svgRef }) => {
  const { t, lang } = usePulseLocale();
  const T = tokens(dm);
  const ref = useRef(null);
  const [width, setWidth] = useState(960);
  useEffect(() => {
    if (!ref.current) return undefined;
    const ro = new ResizeObserver((e) => setWidth(Math.max(320, Math.floor(e[0].contentRect.width))));
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);

  const geom = useMemo(() => {
    const m = { l: 56, r: 28, t: 18, b: 54 };
    const h = Math.min(820, Math.max(440, Math.round(width * 0.78)));
    const iw = width - m.l - m.r, ih = h - m.t - m.b;
    const dom = domainRows && domainRows.length ? domainRows : rows;
    if (!rows.length || !dom.length) return { m, h, iw, ih, pts: [], xt: [], yt: [], labels: {} };
    const dom_ = chartDomain(dom);
    const box = { l: m.l, t: m.t, iw, ih };
    const { x, y, rad, xt, yt } = chartScales(box, dom_);
    const pts = toPoints(rows, box, { x, y, rad });
    const maxN = dom_.maxN;
    return { m, h, iw, ih, pts, xt, yt, x, y, rad, maxN, labels: placeLabels(pts) };
  }, [rows, domainRows, width]);

  const { m, h, iw, ih, pts, xt, yt, labels } = geom;
  const legendVals = geom.maxN >= 60e6 ? [5e6, 20e6, 70e6] : geom.maxN >= 25e6 ? [2e6, 10e6, 30e6] : [1e6, 5e6, 15e6];
  const faded = (p) => highlightIt && p.country_iso2 !== "IT";
  const dark = dm ? "#0F1115" : "#FFFFFF";

  return (
    <div ref={ref} className="w-full" data-testid="cities-chart">
      <svg ref={svgRef} width={width} height={h} role="img" aria-label={t("cities_title", { n: rows.length, year })}
        xmlns="http://www.w3.org/2000/svg" style={{ display: "block", fontFamily: "inherit" }}>
        {/* grid */}
        {xt.map((v) => (
          <g key={`x${v}`}>
            <line x1={geom.x(v)} x2={geom.x(v)} y1={m.t} y2={m.t + ih} stroke={T.border} strokeWidth={1} />
            <text x={geom.x(v)} y={m.t + ih + 16} textAnchor="middle" fontSize={11} fill={T.muted}>{v}</text>
          </g>
        ))}
        {yt.map((v) => (
          <g key={`y${v}`}>
            <line x1={m.l} x2={m.l + iw} y1={geom.y(v)} y2={geom.y(v)} stroke={T.border} strokeWidth={1} />
            <text x={m.l - 8} y={geom.y(v) + 4} textAnchor="end" fontSize={11} fill={T.muted}>{v}</text>
          </g>
        ))}
        <text x={m.l + iw} y={h - 8} textAnchor="end" fontSize={11} fontWeight={600} fill={T.text}>{t("cities_x", { year })}</text>
        <text transform={`translate(14 ${m.t + ih / 2}) rotate(-90)`} textAnchor="middle" fontSize={11} fontWeight={600} fill={T.text}>{t("cities_y")}</text>
        {/* bubbles: biggest first so small ones stay clickable on top; position via transform so frames animate */}
        {[...pts].sort((a, b) => b.r - a.r).map((p) => (
          <g key={p.city} className="pulse-bubble" style={{ transform: `translate(${p.cx}px, ${p.cy}px)`, transition: MOVE }}>
            <circle cx={0} cy={0} r={p.r} data-testid={`cities-bubble-${p.city}`}
              fill={BUCKET_COLORS[p.bucket]} fillOpacity={faded(p) ? 0.18 : 0.82}
              stroke={highlightIt && p.country_iso2 === "IT" ? T.accent : dark}
              strokeWidth={highlightIt && p.country_iso2 === "IT" ? 2.5 : 1.2}
              style={{ transition: MOVE, cursor: "pointer" }}
              onMouseEnter={(e) => onHover && onHover(p, e)} onMouseMove={(e) => onHover && onHover(p, e)}
              onMouseLeave={() => onHover && onHover(null)} onClick={() => onSelect && onSelect(p)}>
              <title>{`${p.name} (${p.country_iso2}) — ${fmtCompact(p.nights_total, lang)} ${t("cities_tt_nights").toLowerCase()}, ${fmtNum(p.foreign_share_pct, lang, 1)}% ${t("cities_tt_foreign").toLowerCase()}, ${fmtNum(p.nights_per_resident, lang, 1)} ${t("cities_tt_npr").toLowerCase()}`}</title>
            </circle>
            {marked && (marked.a === p.city || marked.b === p.city) && (
              <circle cx={0} cy={0} r={p.r + 5} fill="none" stroke={marked.a === p.city ? (dm ? "#33C6D4" : "#006874") : (dm ? "#F0717B" : "#C8333F")}
                strokeWidth={2} strokeDasharray="4 3" pointerEvents="none" data-testid={`cities-marked-${marked.a === p.city ? "a" : "b"}`} style={{ transition: MOVE }} />
            )}
          </g>
        ))}
        {pts.map((p) => labels[p.city] && (
          <g key={`l${p.city}`} style={{ transform: `translate(${p.cx + labels[p.city].dx}px, ${p.cy + labels[p.city].dy}px)`, transition: MOVE, pointerEvents: "none" }}>
            <text data-city={p.city} x={0} y={0} textAnchor={labels[p.city].anchor}
              fontSize={11} fontWeight={p.country_iso2 === "IT" && highlightIt ? 800 : 600}
              fill={faded(p) ? T.muted : T.text} fillOpacity={faded(p) ? 0.55 : 1}
              style={{ paintOrder: "stroke", stroke: dark, strokeWidth: 3, strokeLinejoin: "round" }}>
              {p.name}
            </text>
          </g>
        ))}
      </svg>
      {/* legends (HTML → responsive) */}
      <div className="flex flex-wrap items-start gap-8 mt-2 px-1" data-testid="cities-legend">
        <div>
          <div className="text-[11px] font-semibold mb-1" style={{ color: T.text }}>{t("cities_legend_size", { year })}</div>
          <svg width={150} height={2 * geom.rad(legendVals[2]) + 8} aria-hidden="true">
            {legendVals.slice().reverse().map((v) => {
              const R = geom.rad(legendVals[2]), r = geom.rad(v), cx = R + 2, cy = 2 * R + 4 - r;
              return (
                <g key={v}>
                  <circle cx={cx} cy={cy} r={r} fill="none" stroke={T.muted} strokeWidth={1} />
                  <line x1={cx} x2={2 * R + 30} y1={cy - r} y2={cy - r} stroke={T.border} strokeDasharray="2 2" />
                  <text x={2 * R + 34} y={cy - r + 4} fontSize={10} fill={T.muted}>{Math.round(v / 1e6)}</text>
                </g>
              );
            })}
          </svg>
        </div>
        <div>
          <div className="text-[11px] font-semibold mb-1" style={{ color: T.text }}>{t("cities_legend_npr")}</div>
          <ul className="flex flex-col gap-0.5">
            {BUCKET_KEYS.filter((k) => k !== "under_2" || rows.some((r) => r.bucket === "under_2")).map((k) => (
              <li key={k} className="flex items-center gap-2 text-[11px]" style={{ color: T.muted }}>
                <span className="inline-block w-3 h-3 rounded-full" style={{ background: BUCKET_COLORS[k] }} aria-hidden="true" />
                {t(`bucket_${k}`)}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

/** Mobile fallback (< 640px): ranked rows, bar length = nights per resident on a doubling scale. */
export const CityRankedBars = ({ rows, highlightIt, dm }) => {
  const { t, lang } = usePulseLocale();
  const T = tokens(dm);
  const lo = Math.floor(log2(Math.min(...rows.map((r) => r.nights_per_resident)) * 0.85));
  const hi = Math.ceil(log2(Math.max(...rows.map((r) => r.nights_per_resident)) * 1.15));
  return (
    <div data-testid="cities-ranked-bars">
      <p className="text-[11px] mb-3" style={{ color: T.muted }}>{t("cities_mobile_hint")}</p>
      <ol className="flex flex-col gap-2">
        {rows.map((r) => {
          const w = ((log2(r.nights_per_resident) - lo) / (hi - lo)) * 100;
          const dim = highlightIt && r.country_iso2 !== "IT";
          return (
            <li key={r.city} className="text-xs" style={{ opacity: dim ? 0.45 : 1 }} data-testid={`cities-bar-${r.city}`}>
              <div className="flex items-baseline justify-between gap-2">
                <span className="font-semibold truncate" style={{ color: T.text }}>
                  <span className="tabular-nums mr-1.5" style={{ color: T.muted }}>{r.rank}</span>{r.name} <span style={{ color: T.muted }}>· {r.country_iso2}</span>
                </span>
                <span className="tabular-nums whitespace-nowrap" style={{ color: T.muted }}>
                  {fmtCompact(r.nights_total, lang)} · {fmtNum(r.foreign_share_pct, lang, 0)}%
                </span>
              </div>
              <div className="h-2 mt-1 w-full" style={{ background: T.surface2 }}>
                <div className="h-2" style={{ width: `${Math.max(2, w)}%`, background: BUCKET_COLORS[r.bucket], transition: "width .6s" }} />
              </div>
              <div className="text-[10px] tabular-nums mt-0.5" style={{ color: T.muted }}>{fmtNum(r.nights_per_resident, lang, 1)} {t("cities_tt_npr").toLowerCase()}</div>
            </li>
          );
        })}
      </ol>
    </div>
  );
};
